import { useState, useEffect, useMemo, useRef } from "react"

import ErrorMessage from "components/forms/error-message"
import lodash from "lodash"
import deepdash from "deepdash"
import { lang } from "config"
import Label from "components/forms/label"
import Input from "components/forms/input"
import useProductLineRepositories from "repositories/product-line"
import useProductSeriesRepositories from "repositories/product-series"
import Description from "components/forms/description"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import SelectDescription from "components/forms/select-description"
import { usePage } from "contexts/page-context"
import SelectSearchable from "components/forms/select-searchable"
import { X } from "components/icons/outline"
import AddTag from "./tags/add"
import Textarea from "components/forms/textarea"

const ACCEPTED = [
  "image/jpeg","image/png",
  "video/mp4"
];
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB (kalau sering upload video, pertimbangkan 50MB)

const VIDEO_EXT_RE = /\.(mp4|webm|ogg)(\?|#|$)/i;
const IMAGE_EXT_RE = /\.(jpg|jpeg|png|webp|avif)(\?|#|$)/i;

const visibleCount = (arr) => arr.filter(x => x._action !== "delete").length;
const MAX_MEDIA = 15;

function inferMediaTypeFromPath(path = "") {
  if (VIDEO_EXT_RE.test(path)) return { media_type: "video", mime_type: "video/*" };
  if (IMAGE_EXT_RE.test(path)) return { media_type: "image", mime_type: "image/*" };
  return { media_type: "image", mime_type: "" }; // default aman
}

function isVideo(row) {
  return row?.mime_type?.startsWith("video/")
      || VIDEO_EXT_RE.test(row?.file?.name || row?.file_path || row?._preview || "");
}

// === YouTube helpers ===
const YT_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

function getYouTubeId(url = "") {
  const m = String(url).match(YT_RE);
  return m ? m[1] : null;
}

function youTubeThumb(url = "") {
  const id = getYouTubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
}

function isYouTube(row) {
  return row?.mime_type === "video/youtube"
      || row?.provider === "youtube"
      || !!getYouTubeId(row?.external_url || row?.file_path || row?._preview || "");
}

export default function Form({ data, errors, onSubmit }) {
    const _ = deepdash(lodash)

    const { configuration } = usePage()

    const { data: productSeriesSelection, isLoading: isLoadingProductSeriesSelection } = useProductSeriesRepositories({
        paginate: false,
        order_by: "series.name",
        direction: "asc"
    })

    const id = data?.id

    const [name, setName] = useState(data?.name ?? null)
    const [longName, setLongName] = useState(data?.long_name ?? null)
    const [description, setDescription] = useState(data?.description ?? null)
    const [productSeries, setProductSeries] = useState(data?.product_series?.id ?? null)

    const [productSeriesObject, setProductSeriesObject] = useState()

    const [tags, setTags] = useState(data?.tags ?? [])

    const getKey = (it) => it.id ?? it._key ?? it._preview ?? `${it.file_path || ''}-${it.sort_number}`;

    // === Multiple Images state ===
    const [images, setImages] = useState(() => {
        const list = data?.images || data?.product_model_images || [];
        return (list || []).map((it, i) => {
            const guess = inferMediaTypeFromPath(it.file_path || "");
            return {
            id: it.id ?? null,
            file_path: it.file_path ?? null,
            file: null,
            alt_text: it.alt_text ?? "",
            is_cover: !!it.is_cover,
            sort_number: Number.isFinite(it.sort_number) ? it.sort_number : i,
            _action: "none",
            _preview: it.file_path ? it.file_path : null,
            _key: it.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            // ⬇ penting untuk preview video:
            mime_type: it.mime_type || guess.mime_type,
            media_type: it.media_type || guess.media_type,
            };
        });
    });

    const mediaInputRef = useRef(null);
    const [pickedLabel, setPickedLabel] = useState("Belum ada file dipilih");
    const [ytUrl, setYtUrl] = useState("");

    const usedSlot = useMemo(() => visibleCount(images), [images]);
    const remainingSlot = useMemo(() => Math.max(0, MAX_MEDIA - usedSlot), [usedSlot]);

    const openPicker = () => mediaInputRef.current?.click();


    useEffect(() => {
        if (!data) return;
        const list = data?.images || data?.product_model_images || [];
        setImages((list || []).map((it, i) => {
            const guess = inferMediaTypeFromPath(it.file_path || "");
            return {
            id: it.id ?? null,
            file_path: it.file_path ?? null,
            file: null,
            alt_text: it.alt_text ?? "",
            is_cover: !!it.is_cover,
            sort_number: Number.isFinite(it.sort_number) ? it.sort_number : i,
            _action: "none",
            _preview: it.file_path ? it.file_path : null,
            mime_type: it.mime_type || guess.mime_type,
            media_type: it.media_type || guess.media_type,
            _key: it.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            };
        }));
    }, [data]);

    // helper kecil
    const nextSort = () => (images.length ? Math.max(...images.map(x => x.sort_number)) + 1 : 0);
    const ensureSingleCover = (arr) => {
        const vis = arr.filter(x => x._action !== "delete");
        if (vis.length === 0) return arr;

        // cari cover yg valid (image)
        let coverIdx = vis.findIndex(x => x.is_cover && !isVideo(x));
        if (coverIdx === -1) {
            // jika cover tidak valid / belum ada → ambil image pertama
            coverIdx = vis.findIndex(x => !isVideo(x));
        }

        // kalau tak ada image sama sekali → tak ada cover
        return arr.map((x, i) => {
            const isVisible = x._action !== "delete";
            const wantCover = isVisible && coverIdx !== -1 && (vis[coverIdx] === x);
            return { ...x, is_cover: wantCover };
        });
    };

    useEffect(() => {
        if (data) {
            setName(data.name ?? null)
            setLongName(data.long_name ?? null)
            setDescription(data.description ?? null)
            setProductSeries(data.product_series.id ?? null)
        } else {
            if (!configuration('hierarchy').find((row) => row.key === 'product-series').value && configuration('hierarchy').find((row) => row.key === 'product-model').default_parent === 'auto' && !isLoadingProductSeriesSelection) {
                setProductSeries(productSeriesSelection[0]?.id)
            }
        }
    }, [data, isLoadingProductSeriesSelection])

    useEffect(() => {
        if (productSeries && !isLoadingProductSeriesSelection) {
            setProductSeriesObject(productSeriesSelection.find((row) => row.id === productSeries))
        }
    }, [productSeries, isLoadingProductSeriesSelection])

    useEffect(() => {
        if (!data) {
            setLongName(name)
        }
    }, [name])

    const handleSubmit = (e) => {
        e.preventDefault();

        // pastikan ada minimal 1 IMAGE
        if (!hasImage) {
            toast.error("Tambahkan minimal satu gambar. Video tidak bisa jadi cover.");
            return;
        }
        // pastikan cover adalah IMAGE
        if (!hasImageCover) {
            // coba set image pertama sebagai cover otomatis
            const firstImg = visibleImages.find(x => !isVideo(x));
            if (firstImg) {
                const firstImgKey = getKey(firstImg);
                handleSetCoverByKey(firstImgKey);
                toast.error("Cover otomatis dipindahkan ke gambar pertama.");
            } else {
                // fallback: tidak ada gambar valid (seharusnya tidak terjadi karena ada pengecekan hasImage)
                toast.error("Tidak ada gambar valid untuk dijadikan cover.");
            }
            return;
        }

        if (!nProgress.isStarted()) {
            onSubmit({ id, name, long_name: longName,description, product_series_id: productSeries,
            product_series: productSeriesObject, tags, images });
        } else {
            toast.error("Previous action is loading");
        }
    };


    const handleAddTag = (data) => {
        console.log(data, "data_variant")
        setTags([...tags, ...data.product_type_rows, ...data.variant_rows])
    }

    const handleRemoveTag = (index) => {
        const value = tags.filter((row, i) => i !== index)

        setTags(value)
    }

    useEffect(() => {
        return () => {
            images.forEach(it => {
            if (it._preview?.startsWith("blob:")) URL.revokeObjectURL(it._preview);
            });
        };
    }, []);

    const handleAddMediaFiles = (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;

        const bad = files.find(f => !ACCEPTED.includes(f.type) || f.size > MAX_SIZE);
        if (bad) {
            toast.error("File tidak valid (format jpg/png/mp4, ukuran max 10MB).");
            return;
        }

        // limit 15 media (yang terlihat)
        const current = visibleCount(images);
        const remaining = MAX_MEDIA - current;
        if (remaining <= 0) {
            toast.error(`Batas maksimal ${MAX_MEDIA} media tercapai.`);
            return;
        }

        // jika lebih dari sisa slot, potong
        const filesAllowed = files.slice(0, remaining);
        if (filesAllowed.length < files.length) {
            toast.error(`Hanya ${remaining} file yang ditambahkan (maksimal ${MAX_MEDIA}).`);
        }

        const baseSort = nextSort();
        const newItems = filesAllowed.map((f, i) => {
        const isVid = f.type.startsWith("video/");
        return {
            id: null,
            file_path: null,
            file: f,
            alt_text: "",
            is_cover: false,
            sort_number: baseSort + i,
            _action: "create",
            _preview: URL.createObjectURL(f),
            _key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            mime_type: f.type,
            media_type: isVid ? "video" : "image",
        };
        });

        // gabung & normalize cover (image only)
        const merged = ensureSingleCover(
        [...images, ...newItems].sort((a, b) => a.sort_number - b.sort_number)
        );
        setImages(merged);
    };

    const handleAddYouTube = () => {
        const url = (ytUrl || "").trim();
        if (!url) return;

        const id = getYouTubeId(url);
        if (!id) {
            toast.error("URL YouTube tidak valid.");
            return;
        }

        // limit MAX_MEDIA (yang terlihat)
        const current = visibleCount(images);
        if (MAX_MEDIA - current <= 0) {
            toast.error(`Batas maksimal ${MAX_MEDIA} media tercapai.`);
            return;
        }

        // cegah duplikat video yang sama
        const exists = images.some(
            (x) => x._action !== "delete" &&
            getYouTubeId(x.external_url || x.file_path || x._preview || "") === id
        );
        if (exists) {
            toast.error("Video YouTube ini sudah ditambahkan.");
            return;
        }

        const item = {
            id: null,
            file_path: null,
            file: null,
            external_url: `https://www.youtube.com/watch?v=${id}`,
            provider: "youtube",
            alt_text: "",
            is_cover: false,
            sort_number: nextSort(),
            _action: "create",
            _preview: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
            _key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            mime_type: "video/youtube",
            media_type: "video",
        };

        setImages(ensureSingleCover([...images, item].sort((a, b) => a.sort_number - b.sort_number)));
        setYtUrl("");
    };

    const handleRemoveImageByKey = (key) => {
        const idx = images.findIndex(x => getKey(x) === key);
        if (idx === -1) return;

        const draft = [...images];
        const target = draft[idx];

        if (target.id) {
            // existing → tandai delete
            draft[idx] = { ...target, _action: "delete", is_cover: false };
        } else {
            // baru upload → buang dari list
            draft.splice(idx, 1);
        }

        // pastikan masih ada cover di item yang terlihat (non-delete)
        const filtered = draft.filter(x => x._action !== "delete");
        if (filtered.length && !filtered.some(x => x.is_cover)) {
            filtered[0].is_cover = true;
            // commit balik perubahan cover ke draft (sinkron berdasarkan key)
            const firstKey = getKey(filtered[0]);
            const j = draft.findIndex(x => getKey(x) === firstKey);
            if (j !== -1) draft[j] = { ...draft[j], is_cover: true, _action: draft[j].id && draft[j]._action === "none" ? "update" : draft[j]._action };
        }
        const normalized = ensureSingleCover(filtered);

        setImages(
        // gabungkan normalized (yang visible) ke draft dengan key
        draft.map(x => {
            const rep = normalized.find(y => getKey(y) === getKey(x));
            return rep ? { ...x, is_cover: rep.is_cover } : x;
        })
        );
    };

    const handleSetCoverByKey = (key) => {
        const row = images.find(x => getKey(x) === key);
        if (!row) return;
        if (isVideo(row)) {
            toast.error("Video tidak dapat dijadikan cover. Pilih gambar.");
            return;
        }
        const draft = images.map(x => {
            const isTarget = getKey(x) === key;
            const next = { ...x, is_cover: isTarget };
            if (x.id && (isTarget || x.is_cover)) {
            next._action = next._action === "none" ? "update" : next._action;
            }
            return next;
        });
        setImages(draft);
    };


    const handleAltChangeByKey = (key, val) => {
    setImages(prev =>
        prev.map(it => {
        if (getKey(it) !== key) return it;
        return {
            ...it,
            alt_text: val,
            _action: it.id ? (it._action === "none" ? "update" : it._action) : it._action,
        };
        })
    );
    };

    const handleMoveByKey = (key, dir) => {
        // kerja di visible list
        const visible = images.filter(x => x._action !== "delete").sort((a,b) => a.sort_number - b.sort_number);
        const i = visible.findIndex(x => getKey(x) === key);
        if (i === -1) return;

        const swapWith = dir === "up" ? i - 1 : i + 1;
        if (swapWith < 0 || swapWith >= visible.length) return;

        // tukar sort_number
        const a = { ...visible[i] };
        const b = { ...visible[swapWith] };
        const tmp = a.sort_number; a.sort_number = b.sort_number; b.sort_number = tmp;

        // tandai update kalau existing
        [a, b].forEach(item => {
            if (item.id && item._action === "none") item._action = "update";
        });

        // commit ke images asli via key
        const next = images.map(x => {
            if (getKey(x) === getKey(a)) return a;
            if (getKey(x) === getKey(b)) return b;
            return x;
        });

        setImages(next);
    };

    const visibleImages = images
    .filter(x => x._action !== "delete") // sembunyikan yang sudah dihapus
    .sort((a, b) => a.sort_number - b.sort_number);

    const findSubmitIndexByKey = (key) =>
    visibleImages.findIndex(x => getKey(x) === key);

    const hasImage = visibleImages.some(x => !isVideo(x));
    const hasImageCover = visibleImages.some(x => !isVideo(x) && x.is_cover);

    // Ambil error dari objek errors backend (flat key "images.N.field")
    const getImageFieldError = (rowKey, field) => {
        const submitIdx = findSubmitIndexByKey(rowKey);
        if (submitIdx < 0) return null;
        const flatKey = `images.${submitIdx}.${field}`;
        // Laravel biasanya kirim array of messages
        return errors?.[flatKey]?.[0] ?? null;
    };

    return (
        <form onSubmit={handleSubmit} className="mt-8 space-y-8">
            <div className="mt-10 sm:mt-0">
                <div className="md:grid md:grid-cols-3 md:gap-6">
                    <div className="md:col-span-1">
                        <div className="px-4 sm:px-0">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">{lang.main_form}</h3>
                            <p className="mt-1 text-sm text-gray-600">{lang.please_fill_the_provided_form_input}</p>
                        </div>
                    </div>
                    <div className="mt-5 md:col-span-2 md:mt-0">
                        <div className="border sm:rounded-xl">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="grid grid-cols-2 gap-4">
                                    {configuration('hierarchy').find((row) => row.key === 'product-series').value && (
                                        <>
                                            <div>
                                                <Label htmlFor="series" value="Product Series" />
                                                <SelectSearchable filter={(value) => value.series?.name} onChange={(value) => setProductSeries(value)} selection={productSeriesSelection} isLoading={isLoadingProductSeriesSelection} value={productSeries} keyValue={(value) => value.id} title={(value) => value.series?.name} description={(value) => `${value.product_brand?.product_type?.name} – ${value.product_brand?.brand?.name}`} placeholder="Select Product Series" error={errors.product_series_id} reverse />
                                                <ErrorMessage error={errors.product_series_id} />
                                            </div>
                                            <div></div>
                                        </>
                                    )}
                                    <div>
                                        <Label htmlFor="name" value="Name" />
                                        <Input placeholder="Input name here" onChange={(e) => setName(e.target.value)} value={name} id="name" error={errors.name} />
                                        <Description error={errors.name} />
                                    </div>
                                    <div>
                                        <Label htmlFor="long_name" value="Long Name" />
                                        <Input placeholder="Input long name here" onChange={(e) => setLongName(e.target.value)} value={longName} id="long_name" error={errors.long_name} />
                                        <Description error={errors.long_name} />
                                    </div>
                                    <div>
                                        <Label htmlFor="description" value="Description" />
                                        <Textarea disabled={configuration('site') === 'go'} placeholder="This product contain happiness" onChange={(e) => setDescription(e.target.value)} value={description} id="description" error={errors.description} />
                                        <Description error={errors.description} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="hidden sm:block" aria-hidden="true">
                <div className="py-5"><div className="border-t border-gray-200" /></div>
            </div>

            <div className="mt-10 sm:mt-0">
                <div className="md:grid md:grid-cols-3 md:gap-6">
                    <div className="md:col-span-1">
                        <div className="px-4 sm:px-0">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">Product Media</h3>
                            <p className="mt-1 text-sm text-gray-600">Unggah banyak gambar atau video (Max 15), atur cover, urutan, dan alt text.</p>
                            <p className="text-[11px] text-neutral-500">
                                Format didukung: JPG, PNG dan MP4. Ukuran disarankan &ge; 800x800 px. Hanya satu gambar yang ditandai sebagai <em>cover</em>.
                            </p>
                        </div>
                    </div>
                    <div className="mt-5 md:col-span-2 md:mt-0">
                        <div className="border sm:rounded-xl">
                            <div className="px-4 py-5 space-y-4 sm:p-6">
                            {errors?.message && (
                            <div className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                                {errors.message}
                            </div>
                            )}
                            {/* Uploader */}
                            <div className="flex items-start gap-3">
                                {/* input asli disembunyikan */}
                                <input
                                    ref={mediaInputRef}
                                    id="media"
                                    type="file"
                                    accept="image/*,video/*"
                                    multiple
                                    className="sr-only"
                                    onChange={(e) => {
                                    const files = Array.from(e.target.files || []);
                                    if (files.length) {
                                        setPickedLabel(
                                        files.length === 1 ? files[0].name : `${files.length} file dipilih`
                                        );
                                    }
                                    handleAddMediaFiles(e.target.files);

                                    // reset supaya bisa pilih file yang sama lagi
                                    e.target.value = "";
                                    }}
                                />

                                {/* tombol custom */}
                                <button
                                    type="button"
                                    onClick={openPicker}
                                    disabled={remainingSlot <= 0}
                                    className="inline-flex items-center justify-center rounded-lg bg-neutral-900 px-4 py-2 text-xs font-medium text-white shadow-sm transition hover:bg-neutral-800 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {remainingSlot <= 0 ? "Slot Penuh" : "Pilih Media"}
                                </button>

                                {/* info file + slot */}
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-neutral-800 truncate">
                                    {pickedLabel}
                                    </div>
                                    <div className="mt-0.5 text-[11px] text-neutral-500">
                                    JPG/PNG/MP4 • Max 10MB • Terpakai {usedSlot}/{MAX_MEDIA} • Sisa {remainingSlot}
                                    </div>
                                </div>
                                </div>

                            {/* YouTube URL */}
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                <input
                                    type="url"
                                    value={ytUrl}
                                    onChange={(e) => setYtUrl(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleAddYouTube();
                                        }
                                    }}
                                    placeholder="Tempel URL YouTube (mis. https://youtu.be/xxxx)"
                                    className="block w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring focus:ring-neutral-200"
                                />
                                <button
                                    type="button"
                                    onClick={handleAddYouTube}
                                    disabled={remainingSlot <= 0}
                                    className="inline-flex items-center justify-center px-4 py-2 text-xs font-medium text-white transition rounded-lg shadow-sm whitespace-nowrap bg-red-600 hover:bg-red-700 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Tambah Video YouTube
                                </button>
                            </div>

                            {errors?.images && <ErrorMessage error={errors.images} />}

                            {/* List */}
                            <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                                <table className="min-w-full divide-y divide-neutral-200">
                                <thead className="bg-neutral-50">
                                    <tr>
                                    <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Preview</th>
                                    <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Alt Text</th>
                                    <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Cover</th>
                                    <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Urutan</th>
                                    <th className="px-4 py-3 text-xs font-medium text-right uppercase text-neutral-500 whitespace-nowrap">{lang.action}</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-neutral-200">
                                    {visibleImages.length === 0 && (
                                    <tr className="text-center">
                                        <td colSpan="5" className="px-6 py-6 text-xs text-neutral-500 whitespace-nowrap">
                                        {lang.no_data}
                                        </td>
                                    </tr>
                                    )}

                                    {visibleImages.map((row, index) => {
                                    const rowKey = getKey(row);
                                    const altErr = getImageFieldError(rowKey, 'alt_text');
                                    return (
                                    <tr key={(row.id ?? row._preview) + "-" + index}>
                                        <td className="px-4 py-3 text-xs whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                            <div className="w-16 h-16 overflow-hidden border rounded-lg bg-neutral-50 relative">
                                            {(() => {
                                                if (isYouTube(row)) {
                                                    const ytSrc = youTubeThumb(row.external_url || row.file_path || row._preview);
                                                    const ytLink = row.external_url || row.file_path;
                                                    return (
                                                        <a href={ytLink || undefined} target="_blank" rel="noreferrer" className="relative block w-full h-full" title="Buka di YouTube">
                                                            {ytSrc ? (
                                                                <img src={ytSrc} alt="" className="object-cover w-full h-full" loading="lazy" referrerPolicy="no-referrer" />
                                                            ) : (
                                                                <div className="flex items-center justify-center w-full h-full text-[10px] text-neutral-400">No Preview</div>
                                                            )}
                                                            <span className="absolute bottom-0 inset-x-0 text-center text-[8px] font-semibold text-white bg-red-600/90">YouTube</span>
                                                        </a>
                                                    );
                                                }
                                                if (!row._preview) {
                                                    return (
                                                        <div className="flex items-center justify-center w-full h-full text-[10px] text-neutral-400">No Preview</div>
                                                    );
                                                }
                                                if (isVideo(row)) {
                                                    return (
                                                        <video
                                                            src={row._preview}
                                                            className="object-cover w-full h-full"
                                                            muted
                                                            autoPlay={false}
                                                            loop
                                                            playsInline
                                                        />
                                                    );
                                                }
                                                return (
                                                    <img
                                                        src={row._preview}
                                                        alt=""
                                                        className="object-cover w-full h-full"
                                                        loading="lazy"
                                                        referrerPolicy="no-referrer"
                                                    />
                                                );
                                            })()}
                                            </div>
                                            <div className="text-[10px] text-neutral-500 w-52 whitespace-break-spaces line-clamp-3">
                                            {row.file ? row.file.name : (row.file_path || "—")}
                                            </div>
                                        </div>
                                        </td>
                                        <td className="px-4 py-3 text-xs whitespace-nowrap">
                                        <input
                                            type="text"
                                            className="block w-56 px-2 py-1 text-xs border rounded-lg focus:outline-none focus:ring focus:ring-neutral-200"
                                            placeholder="Alt text (SEO)"
                                            value={row.alt_text ?? ""}
                                            onChange={(e) => handleAltChangeByKey(getKey(row), e.target.value)}
                                        />
                                        {altErr && (
                                        <div className="mt-1 text-[11px] text-red-600">
                                            {altErr}
                                        </div>
                                        )}
                                        </td>
                                        <td className="px-4 py-3 text-xs whitespace-nowrap">
                                        <label className="inline-flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="cover"
                                                checked={!!row.is_cover}
                                                disabled={isVideo(row)}
                                                onChange={() => handleSetCoverByKey(getKey(row))}
                                            />
                                            <span className={isVideo(row) ? "text-neutral-400" : ""}>
                                            {isVideo(row) ? "Cover (gambar saja)" : "Set as cover"}
                                            </span>
                                        </label>
                                        </td>
                                        <td className="px-4 py-3 text-xs whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <button
                                            type="button"
                                            onClick={() => handleMoveByKey(getKey(row), "up")}
                                            className="px-2 py-1 text-xs text-white transition bg-neutral-700 rounded-lg active:hover:scale-95 disabled:opacity-40"
                                            disabled={index === 0}
                                            >
                                            Up
                                            </button>
                                            <button
                                            type="button"
                                            onClick={() => handleMoveByKey(getKey(row), "down")}
                                            className="px-2 py-1 text-xs text-white transition bg-neutral-700 rounded-lg active:hover:scale-95 disabled:opacity-40"
                                            disabled={index === images.filter(x => x._action !== "delete" || x.id).length - 1}
                                            >
                                            Down
                                            </button>
                                        </div>
                                        </td>
                                        <td className="px-4 py-3 text-xs font-medium text-right whitespace-nowrap">
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveImageByKey(getKey(row))}
                                            className="inline-flex items-center px-3 py-1 text-white transition bg-red-700 rounded-full active:hover:scale-95"
                                        >
                                            Remove
                                        </button>
                                        </td>
                                    </tr>
                                    )})}
                                </tbody>
                                </table>
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="hidden sm:block" aria-hidden="true">
                <div className="py-5">
                    <div className="border-t border-gray-200" />
                </div>
            </div>

            <div className="mt-10 sm:mt-0">
                <div className="md:grid md:grid-cols-3 md:gap-6">
                    <div className="md:col-span-1">
                        <div className="px-4 sm:px-0">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">Data Tag</h3>
                            <p className="mt-1 text-sm text-gray-600">List data tag.</p>
                        </div>
                    </div>
                    <div className="mt-5 md:col-span-2 md:mt-0">
                        <div className="border sm:rounded-xl">
                            <div className="px-4 py-5 space-y-4 sm:p-6">
                                <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                                    <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
                                        <thead className="bg-neutral-50 rounded-t-3xl">
                                            <tr>
                                                {/* <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Name</th> */}
                                                <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Slug</th>
                                                {true && (
                                                    <th scope="col" className="relative px-6 py-3"><span className="sr-only">{lang.action}</span></th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-neutral-200">
                                            {tags.length === 0 && (
                                                <tr className="text-center">
                                                    <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                        {lang.no_data}
                                                    </td>
                                                </tr>
                                            )}
                                            {tags.length !== 0 && tags?.map((row, index) => (
                                                <tr key={row}>
                                                    {/* <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                                        {row.name}
                                                    </td> */}
                                                    <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                                        {row.slug}
                                                    </td>
                                                    {true && (
                                                        <td class="px-6 py-4 text-xs font-medium text-right whitespace-nowrap">
                                                            <button type="button" onClick={() => handleRemoveTag(index)} class={`${false ? 'opacity-50 bg-red-900' : 'bg-red-800'} inline-flex items-center p-1 text-white transition rounded-full active:hover:scale-90`}>
                                                                <X className="w-6 h-6" />
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="text-xs">
                                    <AddTag tags={tags} onSubmit={(data) => handleAddTag(data)} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8 text-xs text-right">
                <button type="submit" className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90">
                    <span>{`${_.isEmpty(data) ? lang.create : lang.update}`}</span>
                </button>
            </div>
        </form>
    )
}